import * as Yup from 'yup';
import { endpoints } from 'src/utils/axios';

export const featureConfig = {
    // Entity metadata
    entity: {
        name: 'Feature Config',
        namePlural: 'Feature Configs',
        apiEndpoint: endpoints.featureMaster,
        storageKey: 'feature-master-list-settings',
    },

    // Form configuration   
    form: {
        title: {
            create: 'Create Feature Config',
            edit: 'Edit Feature Config',
        },
        fields: [
            {
                name: 'menu',
                label: 'Menu (Module)',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.menuMaster.list}?status=active&limit=0`,
                optionLabel: (option) => {
                    const name = option?.name || '';
                    const moduleName = option?.module?.name;
                    return moduleName ? `${name} (${moduleName})` : name;
                },
                optionValue: '_id',
                placeholder: 'Select menu',
                defaultValue: null,
                validation: Yup.mixed().required('Menu is required'),
                gridProps: { xs: 12, sm: 6 },
            },
            {
                name: 'submenu',
                label: 'Submenu',
                type: 'autocomplete',
                // API Endpoint depends on selected menu
                apiEndpoint: (formData) => {
                    const menu = formData?.menu;
                    const menuId = menu?._id || menu;
                    return menuId
                        ? `${endpoints.submenuMaster.list}?status=active&limit=0&menu=${menuId}`
                        : `${endpoints.submenuMaster.list}?status=active&limit=0`;
                },
                optionLabel: 'name',
                optionValue: '_id',
                placeholder: 'Select submenu',
                defaultValue: null,
                gridProps: { xs: 12, sm: 6 },
                dependsOn: 'menu',
                // Disable if selected menu is direct link
                disabled: (formData) => {
                    const menu = formData?.menu;
                    return menu && menu.is_direct_link === true;
                }
            },
            {
                name: 'features',
                label: 'Features',
                type: 'autocomplete',
                multiple: true,
                options: [
                    { label: 'View Self', value: 'view_self' },
                    { label: 'View All', value: 'view_all' },
                    { label: 'Add', value: 'add' },
                    { label: 'Apply Self', value: 'apply_self' },
                    { label: 'Apply All', value: 'apply_all' },
                    { label: 'Edit Self', value: 'edit_self' },
                    { label: 'Edit All', value: 'edit_all' },
                    { label: 'Delete Self', value: 'delete_self' },
                    { label: 'Delete All', value: 'delete_all' },
                    { label: 'Import', value: 'import' },
                    { label: 'Export', value: 'export' },
                    { label: 'Duplicate', value: 'duplicate' },
                    { label: 'Approve Self', value: 'approve_self' },
                    { label: 'Approve', value: 'approve' },
                ],
                placeholder: 'Select features',
                defaultValue: [],
                showSelectAll: true,
                validation: Yup.array().min(1, 'At least one feature is required'),
                gridProps: { xs: 12 },
            },
            {
                name: 'status',
                label: 'Status',
                type: 'select',
                defaultValue: 'active',
                validation: Yup.string().required('Status is required'),
                options: [
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                ],
                gridProps: { xs: 12, sm: 6 },
            },
        ],
        showAvatar: false,
        maxWidth: 'sm',
    },

    // Table configuration
    table: {
        columns: [
            {
                id: 'menu',
                label: 'Menu',
                width: 200,
                sortable: true,
                type: 'text',
                render: (value, row) => `${value?.name || 'N/A'} (${value?.module?.name || 'N/A'})`,
            },
            {
                id: 'submenu',
                label: 'Submenu',
                width: 200,
                sortable: true,
                type: 'text',
                render: (value, row) => value?.name || '-',
            },
            {
                id: 'features',
                label: 'Features',
                width: 300,
                sortable: false,
                type: 'tags', // Assuming tags type exists, or render custom
                render: (value, row) => Array.isArray(value) ? value.join(', ') : '',
            },
            {
                id: 'status',
                label: 'Status',
                width: 120,
                type: 'status',
                sortable: true,
                align: 'center',
                render: (value, row) => {
                    const status = value || 'inactive';
                    return {
                        label: status === 'active' ? 'Active' : 'Inactive',
                        color: status === 'active' ? 'success' : 'error',
                    };
                },
            },
            {
                id: 'createdAt',
                label: 'Created At',
                width: 150,
                type: 'date',
                sortable: true,
                format: 'dd MMM yyyy',
            },
        ],
        defaultSort: { field: 'createdAt', direction: 'desc' },
        rowsPerPageOptions: [5, 10, 25, 50],
        defaultRowsPerPage: 10,
    },

    // Filter configuration
    filters: {
        default: {
            search: '',
            status: 'all',
            menu: null,
        },
        fields: [
            // GenericListView main search (debounced)
            {
                name: 'search',
                // FeatureMasterController doesn't support generic search easily yet (no name field).
                // But GenericListView usually requires it.
                // I'll leave it but expected to not work well unless I update Controller to search menu name via lookup.
                // For now, I'll allow it.
                label: 'Search',
                type: 'search',
                placeholder: 'Search...',
                apiParam: 'search',
            },
            {
                name: 'status',
                label: 'Status',
                type: 'tabs',
                options: [
                    { value: 'all', label: 'All', color: 'default' },
                    { value: 'active', label: 'Active', color: 'success' },
                    { value: 'inactive', label: 'Inactive', color: 'error' },
                ],
                apiParam: 'status',
                showCount: true,
            },
            {
                name: 'menu',
                label: 'Menu',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.menuMaster.list}?status=active&limit=0`,
                optionLabel: 'name',
                optionValue: '_id',
                placeholder: 'Filter by menu',
                apiParam: 'menu',
                inAdvancedFilters: true,
            },
        ],
    },

    // Card view configuration
    card: {
        title: (item) => item.menu?.name || 'N/A',
        subtitle: (item) => `Submenu: ${item.submenu?.name || '-'}`,
        avatar: false,
        status: (item) => {
            const status = item.status || 'inactive';
            return {
                label: status === 'active' ? 'Active' : 'Inactive',
                color: status === 'active' ? 'success' : 'error',
            };
        },
        metadata: [
            {
                label: 'Menu',
                value: (item) => item.menu?.name,
                icon: 'solar:widget-bold-duotone',
            },
            {
                label: 'Features',
                value: (item) => Array.isArray(item.features) ? item.features.length : 0,
                icon: 'solar:widget-bold-duotone',
            },
        ],
    },

    // Normalize data for form (edit mode)
    normalizeDataForForm: (item) => {
        if (!item) return item;
        return {
            ...item,
            status: item.status || 'active',
            menu: item.menu,
            submenu: item.submenu,
        };
    },

    // Permissions & features
    features: {
        create: true,
        edit: true,
        delete: true,
        bulkDelete: true,
        export: false,
        duplicate: true,
        import: false,
        viewToggle: true,
        columnVisibility: true,
    },
};

export default featureConfig;
